// framework/client/core/SessionTokenHandler.js

import { ComponentBase } from "../core/ComponentBase.js";

export class SessionTokenHandler extends ComponentBase {
  constructor(config, appClient) {
    super({ appClient });

    const defaultConfig = {
      sessionTokenName: "sessionToken",
      enabled: false,
      authRpc: "AuthRpc",
      cacheRefreshInterval: 300,
    }
    this.config = { ...defaultConfig, ...config }; // Configuration for the handler
    this.token = null;
    this.cachedPermissions = null;
    this.storage = localStorage;
    this.cacheUpdateInterval = null;
    this._cachedLoginStatus = false; // Initialize cached status to false
    this.appClient.events.local.on("session-update", this.handleSessionUpdate.bind(this));
    this.dynamicSubscriptions = new Map();
    this.isInitialConnect = true;

    this.loadToken();
  }

  async initialize() {
    this.log.info("Initializing Session Token Handler");

    // Fetch configuration from the server
    const authRpcName = this.config.authRpc;

    this.clientConfig = await this.appClient.rpcClient.call(
      `${authRpcName}.getClientConfig`
    );

    // Initialize cached login status
    this._cachedLoginStatus = await this.isLoggedIn();
    await this.updateCachedPermissions();
    this.startPeriodicCacheUpdate();
    this.resetSessionTimeout();
    this.appClient.rpcClient.socket.on("session-update", this.handleSessionUpdate.bind(this));
    this.appClient.rpcClient.socket.on("connect", this.handleServerReconnection.bind(this));

    this.performLoggedInProcess();
  }

  async handleServerReconnection() {
    // If this is the very first time 'connect' fires, the main initialize() flow handles it.
    // We just flip the flag for all subsequent connections.

    // If we reach here, it's a RECONNECTION.
    this.log.info("Socket reconnected to server. Re-validating session and subscriptions...");

    // Check if the user was logged in before the disconnect.
    if (this._cachedLoginStatus) {
      this.log.info("User was previously logged in. Attempting to re-establish session...");

      // Re-validate the session with the server.
      const stillLoggedIn = await this.isLoggedIn();

      if (stillLoggedIn) {
        // If the session is still valid on the server, re-subscribe to everything.
        await this.performLoggedInProcess();
      } else {
        // If the session is no longer valid (e.g., server restarted and lost session state),
        // handle it as an expired session.
        this.log.warn("Session could not be re-validated on server after reconnect. Treating as expired.");
        this.handleSessionExpiry(); // This will clear tokens and redirect to login.
      }
    } else {
      this.log.info("No active login session detected upon reconnection. No action needed.");
    }
  }


  async performLoggedInProcess() {
    if (this._cachedLoginStatus) {
      // 1. Existing static subscriptions
      await this.appClient.rpcClient.call(`${this.config.authRpc}.subscribeToAuthorizedChannels`);

      if (this.dynamicSubscriptions.size > 0) {
        this.log.info(`Re-establishing ${this.dynamicSubscriptions.size} dynamic subscriptions...`);
        // Use Promise.all for concurrent re-subscriptions
        const resubscribePromises = Array.from(this.dynamicSubscriptions.keys()).map(key => this.subscribe(key));
        await Promise.all(resubscribePromises);
      }

    }
  }

  handleSessionUpdate(newSessionToken) {
    this.log.info("Session updated");
    this.setToken(newSessionToken);
    this.resetSessionTimeout();
  }

  loadToken() {
    this.token = this.storage.getItem(this.config.sessionTokenName);
  }

  setToken(token) {
    this.token = token;
    this.storage.setItem(this.config.sessionTokenName, token);
    this.appClient.events.fireLocal("session-updated", { token });
  }

  getToken() {
    return this.token;
  }

  clearToken() {
    this.token = null;
    this.storage.removeItem(this.clientConfig.session.cookieName);
    this.appClient.events.fireLocal("session-cleared");
  }

  async isLoggedIn() {
    if (!this.token) return false;

    try {
      // Call a method on AuthRpc to check if the session is still valid on the server
      const response = await this.appClient.rpcClient.call(`${this.config.authRpc}.isLoggedIn`);
      if (response.isLoggedIn) {
        this.appClient.setSharedData('user', response.user);
        this.appClient.setSharedData('userId', response.user._id);
        this.appClient.setSharedData('subscriptionId', response.user.subscriptionId);

      }
      return response.isLoggedIn;
    } catch (error) {
      this.log.error("Error checking session validity:", error);
      return false;
    }
  }

  startPeriodicCacheUpdate() {
    if (this.clientConfig.session.cacheRefreshInterval) {
      if (this.cacheUpdateInterval) {
        clearInterval(this.cacheUpdateInterval);
      }
      this.cacheUpdateInterval = setInterval(async () => {
        this.log.info("refreshing cache");
        const newStatus = await this.isLoggedIn();
        if (newStatus !== this._cachedLoginStatus) {
          this._cachedLoginStatus = newStatus;
          if (newStatus) {
            this.appClient.setSharedData('user', response.user);
            this.appClient.setSharedData('userId', response.user._id);

            this.appClient.events.fireLocal("USER_LOGGED_IN");
          } else {
            this.appClient.events.fireLocal("USER_LOGGED_OUT");
          }
        }
      }, this.clientConfig.session.cacheRefreshInterval * 1000); // Convert to milliseconds
    }
  }

  getCachedLoginStatus() {
    return this._cachedLoginStatus;
  }

  resetSessionTimeout() {
    if (this.sessionTimeout) {
      clearTimeout(this.sessionTimeout);
    }

    if (this.clientConfig.session.expiry) {
      this.sessionTimeout = setTimeout(() => {
        this.log.info("Session expired");
        this.handleSessionExpiry();
      }, this.clientConfig.session.expiry * 1000);
    }
  }

  handleSessionExpiry() {
    this.clearToken();
    this._cachedLoginStatus = false; // Reset cached status
    if (this.config.onSessionExpired) {
      this.config.onSessionExpired();
    } else {
      // Default behavior: redirect to login
      //window.location.href = "/login";
    }
    this.appClient.events.fireLocal("USER_LOGGED_OUT");
  }

  /**
     * Centralized method to handle the client-side effects of a successful login,
     * whether from a password login or an OAuth callback.
     * @param {object} response - The successful response object from the server.
     * @param {object} response.user - The user document.
     * @param {string} response.token - The new session token.
     * @returns {Promise<void>}
     */
  async processLoginSuccess(response) {
    if (!response || !response.user || !response.token) {
      this.log.error("processLoginSuccess called with invalid response object.", response);
      throw new Error("Invalid login response from server.");
    }

    // 1. Set the token and update shared data (existing logic)
    this.setToken(response.token);
    this._cachedLoginStatus = true;
    this.appClient.setSharedData('user', response.user);
    this.appClient.setSharedData('userId', response.user._id);

    // 2. Subscribe to push notifications (if configured)
    await this.appClient.pushManager.subscribe();

    // 3. Subscribe to real-time server channels
    await this.performLoggedInProcess();

    // 4. Fetch and cache the user's permissions
    await this.updateCachedPermissions();

    // 5. Fire the global login event to notify all components
    this.appClient.events.fireLocal("USER_LOGGED_IN", { user: response.user });

    this.log.info(`Session successfully processed for user: ${response.user.username}`);

    // --- START OF NEW REDIRECTION LOGIC ---
    // 6. Determine the final navigation target
    const onboardingConfig = this.clientConfig.onboarding || { enabled: false };
    const user = response.user;

    // Check if the onboarding wizard is enabled in the app's config AND if the user hasn't completed it yet.
    if (onboardingConfig.enabled && user.onboardingCompleted === false) {
      this.log.info(`User '${user.username}' has not completed onboarding. Redirecting to wizard.`);
      // Navigate to the route specified in the onboarding config.
      this.appClient.layoutEngine.navigate(onboardingConfig.route);
    } else {
      // Onboarding is complete or disabled, proceed to the user's default dashboard.
      this.log.info(`User '${user.username}' is fully onboarded. Redirecting to default dashboard.`);
      const primaryRole = (Array.isArray(user.role) ? user.role[0] : user.role) || 'user';
      const defaultRoute = this.appClient.layoutEngine.defaultRoutes[primaryRole] || this.appClient.layoutEngine.defaultRoutes.user;
      this.appClient.layoutEngine.navigate(defaultRoute);
    }
  }


  async login(identifier, password) {
    try {
      const response = await this.appClient.rpcClient.call(
        `${this.config.authRpc}.login`,
        { identifier, password }
      );

      await this.processLoginSuccess(response);
      return response;
    } catch (error) {
      this.log.error("Login failed:", error);
      throw error;
    }
  }


  async logout() {
    try {
      await this.appClient.pushManager.unsubscribe();

      await this.appClient.rpcClient.call(
        `${this.config.authRpc}.logout`
      );
    } catch (error) {
      this.log.error("Logout failed:", error);
    } finally {
      this.dynamicSubscriptions.clear(); // Clear all registered subscriptions

      this.updateCachedPermissions();
      this._cachedLoginStatus = false;
      this.clearToken();
      this.appClient.events.fireLocal("USER_LOGGED_OUT");
      setTimeout(() => {
        window.location.reload();
      }, 1);
    }
  }

  async signup(userData) {
    try {
      const response = await this.appClient.rpcClient.call(
        `${this.config.authRpc}.signup`,
        userData
      );
      // Handle successful signup (e.g., display a success message, redirect)
      this.log.info("Signup successful:", response);
      return response;
    } catch (error) {
      this.log.error("Signup failed:", error);
      throw error;
    }
  }

  async forgotPassword(email) {
    try {
      const response = await this.appClient.rpcClient.call(
        `${this.config.authRpc}.forgotPassword`,
        { email }
      );
      // Handle successful request (e.g., display a success message)
      this.log.info("Forgot password request successful:", response);
      return response;
    } catch (error) {
      this.log.error("Forgot password request failed:", error);
      throw error;
    }
  }

  async resetPassword({otpRequestId, otpCode, newPassword}) {
    try {
      const response = await this.appClient.rpcClient.call(
        `${this.config.authRpc}.resetPassword`,
        { otpRequestId, otpCode, newPassword }
      );
      // Handle successful password reset (e.g., display a success message, redirect to login)
      this.log.info("Password reset successful:", response);
      return response;
    } catch (error) {
      this.log.error("Password reset failed:", error);
      throw error;
    }
  }

  getClientConfig() {
    return this.clientConfig;
  }

  async updateCachedPermissions() {
    try {
      const permissions = await this.appClient.rpcClient.call(`${this.config.authRpc}.getUserRolePermissions`);
      this.cachedPermissions = permissions;
      const user = this.appClient.getSharedData("user");
      if (user) {
        this.cachedRoles = user.roles ? user.roles : [user.role];
      }

      this.log.info("Cached permissions updated:", this.cachedPermissions);
    } catch (error) {
      this.log.error("Error updating cached permissions:", error);
      // Handle error (e.g., clear cached permissions, show a warning)
      this.cachedPermissions = null;
    }
  }

  async hasPermission(permissionType, permissionTarget) {
    if (!this.cachedPermissions) {
      this.log.warn(`No cached permissions found.  Checking as public for ${permissionType}:${permissionTarget}`);
      return false;
    }

    const permissionsForType = this.cachedPermissions[permissionType];
    if (!permissionsForType) {
      return false; // No permissions of this type at all
    }
    return permissionsForType.some(allowed => this.simpleMatch(permissionTarget, allowed));
  }

  simpleMatch(target, pattern) { // must replace with proper glob matching

    if (pattern === "*.*" || pattern === "*") return true; // Full wildcard
    if (pattern.startsWith("*")) {
      return target.endsWith(pattern.substring(1));
    }
    if (pattern.endsWith("*")) {
      return target.startsWith(pattern.slice(0, -1));
    }
    return target === pattern;
  }

  registerSubscription(key, subscribeRpc, unsubscribeRpc, rpcParams) {
    this.log.info(`Registering dynamic subscription for key: ${key}`);
    this.dynamicSubscriptions.set(key, { subscribeRpc, unsubscribeRpc, rpcParams, isActive: false }); // Add isActive flag
  }



  async subscribe(key) {
    const sub = this.dynamicSubscriptions.get(key);
    if (sub) {
      try {
        this.log.debug(`Executing dynamic subscription for '${key}'...`);
        const context = sub.rpcParams?.projectId ? { projectId: sub.rpcParams.projectId } : {};
        await this.appClient.rpcClient.call(sub.subscribeRpc, sub.rpcParams, context);
        sub.isActive = true; // Mark as active
      } catch (error) {
        this.log.error(`Failed to subscribe for key '${key}':`, error);
      }
    } else {
      this.log.warn(`Attempted to subscribe with an unregistered key: ${key}`);
    }
  }


  async unsubscribe(key) {
    const sub = this.dynamicSubscriptions.get(key);
    if (sub) {
      try {
        this.log.debug(`Executing dynamic unsubscription for '${key}'...`);
        const context = sub.rpcParams?.projectId ? { projectId: sub.rpcParams.projectId } : {};
        await this.appClient.rpcClient.call(sub.unsubscribeRpc, sub.rpcParams, context);
      } catch (error) {
        this.log.error(`Failed to unsubscribe on server for key '${key}':`, error);
      } finally {
        this.dynamicSubscriptions.delete(key);
        this.log.info(`Unregistered dynamic subscription for key: ${key}`);
      }
    }
  }


  getCachedRoles() {
    return this.cachedRoles || ["public"];
  }

}